import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ApiResponse } from '../models/ApiResponse';
import { API_URL } from './api-url';
import { SKIP_GLOBAL_LOADER } from '../core/Loading.interceptor';

export interface MaintenanceStatus {
  enabled: boolean;
  allowed: boolean;
  message: string;
}

const DEFAULT_STATUS: MaintenanceStatus = {
  enabled: false,
  allowed: true,
  message: ''
};

const DEFAULT_MESSAGE = "Stiamo aggiornando l'applicativo. Torna tra pochi minuti.";

@Injectable({
  providedIn: 'root'
})
export class MaintenanceService {

  private readonly http = inject(HttpClient);
  private readonly statusSubject = new BehaviorSubject<MaintenanceStatus>(DEFAULT_STATUS);

  readonly status$ = this.statusSubject.asObservable();

  get currentStatus(): MaintenanceStatus {
    return this.statusSubject.value;
  }

  async loadStatus(): Promise<MaintenanceStatus> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<MaintenanceStatus>>(`${API_URL}/api/system/status`, {
          context: new HttpContext().set(SKIP_GLOBAL_LOADER, true)
        })
      );
      const status = this.normalizeStatus(response.data);
      this.statusSubject.next(status);
      return status;
    } catch {
      this.statusSubject.next(DEFAULT_STATUS);
      return DEFAULT_STATUS;
    }
  }

  activateFromError(message?: string): void {
    this.statusSubject.next({
      enabled: true,
      allowed: false,
      message: message?.trim() || DEFAULT_MESSAGE
    });
  }

  private normalizeStatus(status?: MaintenanceStatus): MaintenanceStatus {
    if (!status) {
      return DEFAULT_STATUS;
    }

    return {
      enabled: Boolean(status.enabled),
      allowed: Boolean(status.allowed),
      message: status.message?.trim() || DEFAULT_MESSAGE
    };
  }
}
